import { createContext, useContext, useEffect, useState } from 'react';
import axiosInstance from '../libs/axiosInstance';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token') || null);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          // Fetch user data from API
          const response = await axiosInstance.get('/account/profile/');
          setUser(response.data?.user);
          setProfile(response.data);
        } catch (error) {
          if (error.response && error.response.status === 401) {
            console.error('Unauthorized access, logging out:', error);
            logout(); // Log out the user if it's a 401 error
          } else {
            console.error('Failed to fetch user data:', error);
          }
        }
      }
      setLoading(false); // Set loading to false after attempt to fetch
    };

    fetchUser();
  }, [token]);

  const register = async (data) => {
    try {
      const response = await axiosInstance.post('/account/create-account/', data);
      setUser(response.data);
      setToken(response.data?.token);
      localStorage.setItem('token', response.data.token);
      return response.data?.user;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error.response?.data || error.message;
    }
  };

  const login = async (data) => {
    try {
      const response = await axiosInstance.post('/account/login/', {
        email: data.email,
        password: data.password,
      });
      setUser(response.data?.user);
      setProfile(response.data);
      setToken(response.data?.token);
      localStorage.setItem('token', response.data.token);
      return response.data?.user;
    } catch (error) {
      console.error('Login failed:', error);
      let errorMessage = error.response?.data?.non_field_errors?.[0] || error.response?.data?.detail || error?.message;
      if(errorMessage == 'Invalid token.') {
        logout(); 
      }
      throw errorMessage;
    }
  };

  const logout = async () => {
    try {
      const response = await axiosInstance.post('/account/logout/');
      navigate('/');
      toast.success('Logout successful');
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    } finally {
      setUser(null);
      setProfile(null);
      setToken(null);
      localStorage.removeItem('token');
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, token, login, logout, register, setUser, setProfile }}>
      {isLoading ? (
        <div className="flex items-center justify-center h-screen">
          <span className="loading loading-dots loading-lg"></span>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
